import {useFormik} from "formik";
import {useAtom} from "jotai";
import {useCallback, useEffect, useState} from "react";
import {PiEnvelopeSimple} from "react-icons/pi";
import {useSelector} from "react-redux";
import {toast} from "sonner";
import * as yup from 'yup';
import {formsSubmitAtom} from "../../../atom/atom";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import PhoneInput from "../../../components/forms/PhoneInputWrapper";
import {usePrivilege} from "../../../components/priviledge/PriviledgeProvider";
import useRoles from "../../../hooks/api-calls/useRoles";
import useFetch from "../../../hooks/useFetch";
import useHandleErrors from "../../../hooks/useHandleErrors";
import {User} from "../../../redux/authSlice";
import {RootState} from "../../../redux/store";
import {OrganisationService} from "../../../services/company/organisationService";
import {ClientService} from "../../../services/clients/clientService";
import useCompanies from "../../../hooks/api-calls/useCompanies";

interface ClientFormProps {
    clientSelected: any;
    formRef?: any;
    setOpenModal: any;
    refetch: () => void;
}

const schema = yup.object({
    companyName: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
    cifNif: yup.string().min(1, 'Demasiado corto').required('Campo obligatorio'),
    address: yup.string().min(1, 'Demasiado corto').required('Campo obligatorio'),
    email: yup.string().email('Email no válido').nullable(),
    telephone: yup.string().nullable()
});

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('Campo obligatorio'),
}));

const ClientForm: React.FC<ClientFormProps> = ({ clientSelected, formRef, setOpenModal, refetch }) => {

    // STATES

    const mode = clientSelected ? 'Editar' : 'Crear';
    const [selectedView, setSelectedView] = useState<string>('mainInfo');
    const [isSearched, setIsSearched] = useState<boolean>(false);
    const [, setIsSubmitting] = useAtom(formsSubmitAtom);

    // HOOKS

    const service = new ClientService();
    const { user } = useSelector((state: RootState) => state.auth);
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();

    // FUNCTIONS


    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL ENVIO DEL FORMULARIO DE EDICION
     * @EN HANDLE THE SUBMIT OF THE EDIT FORM
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleUpdate = async (values: any) => {
        try {
            const response = (await service.editClient(values)).getResponseData();

            if (response.success) {
                toast.success("Cliente editado correctamente");
                refetch();
                setOpenModal(false);
            } else {
                handleErrors(response);
                return;
            }
        } catch (error: any) {
            toast.error("Error al editar el cliente");
        }
    };
    //--------------------------------------------------------------------------------------------------------------


    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL ENVIO DEL FORMULARIO DE CREACION
     * @EN HANDLE THE SUBMIT OF THE CREATE FORM
     *
     * @param values
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = (await service.createClient(values)).getResponseData();

            if (response.success) {
                setOpenModal(false);
                refetch();
                toast.success("Cliente creado correctamente");
            } else {
                handleErrors(response);
                return;
            }
        } catch (error: any) {
            toast.error("Error al crear el cliente");
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // FORMIK
    const formik = useFormik({
        initialValues: {
            id: clientSelected?.id ?? '',
            company: clientSelected?.company?.id ?? '',
            companyName: clientSelected?.companyName ?? '',
            cifNif: clientSelected?.cifNif ?? '',
            address: clientSelected?.address ?? '',
            email: clientSelected?.email ?? '',
            telephone: clientSelected?.telephone ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (clientSelected) {
                handleUpdate(values);
            } else {
                handleCreate(values);
            }
        },
    });

    // USE EFFECTS

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList()?.length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className="mb-10 grid gap-4 divide-y-2 divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11 font-medium">
                    {
                        selectedView === 'mainInfo' && (
                            <div className={'md:col-span-12'}>
                                <div className='md:grid md:grid-cols-12 gap-4'>
                                    <RequiredInput
                                        id='companyName'
                                        label="Razón social"
                                        className='md:col-span-12'
                                        formik={formik}
                                        error={formik.errors.companyName}
                                    />

                                    <RequiredInput
                                        id='cifNif'
                                        label="CIF/NIF"
                                        className='md:col-span-12'
                                        formik={formik}
                                        error={formik.errors.cifNif}
                                    />

                                    <RequiredInput
                                        required={true}
                                        id='address'
                                        label="Dirección"
                                        className='md:col-span-12'
                                        formik={formik}
                                        error={formik.errors.address}
                                    />

                                    <PhoneInput
                                        id='telephone'
                                        label='Teléfono'
                                        className="md:col-span-12"
                                        onBlur={formik.handleBlur}
                                        onChange={e => { formik.setFieldValue('telephone', e) }}
                                        value={formik.values.telephone}
                                        error={formik.errors.telephone as string}
                                        required={false}
                                    />

                                    <RequiredInput
                                        required={false}
                                        id='email'
                                        type="email"
                                        label="Correo Electrónico"
                                        prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
                                        className='md:col-span-12'
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                    />

                                    {userCan('list', 'company') && (
                                        <CustomSelect
                                            required
                                            isSearchable
                                            id={'company'}
                                            label="Compañia"
                                            containerClassName='md:col-span-12'
                                            value={
                                                formik.values.company ?
                                                { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                                getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                                            }
                                            options={getCompaniesList()}
                                            onChange={
                                                (e: any) => { formik.setFieldValue('company', e?.value) }
                                            }
                                            error={formik.errors.company}
                                        />
                                    )}
                                </div>
                            </div>
                        )
                    }
                </div>
            </form>
        </div>
    )
}

export default ClientForm;